import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import styled from "@emotion/styled";
import auth0 from "auth0-js";
import { useDispatch } from "react-redux";
import { updateUser } from "state/reducers/userReducer";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import { LogoutCallBack } from "utils/auth0";
import { getAdminUserInfo } from "services/user";
import { SESSION_VARIABLES } from "constants/system";
import { getAdminUserPermissions } from "services/admin";
import { verifyResponse } from "utils/httpRequest";
import { useCmsColorMode } from "context/CmsColorModeContext";

const StyledHeader = styled.div`
  display: flex;
  width: 100%;
  height: 64px;
  background: var(--cms-header-bg);
  color: var(--cms-header-text);
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--cms-header-border);
  box-shadow: var(--cms-header-shadow);

  .logo {
    img {
      width: 160px;
      margin-left: 20px;
    }
  }

  .header-content {
    display: flex;
    align-items: center;
    color: var(--cms-header-text);
    font-size: 14px;
    gap: 12px;
    margin-right: 18px;

    a {
      color: var(--cms-header-text);
      font-size: 14px;
    }
    & > div {
      margin-right: 10px;
    }

    .theme-toggle-btn {
      border-color: var(--cms-header-action-border);
      background: var(--cms-header-action-bg);
      color: var(--cms-header-text);
      width: 36px;
      height: 36px;
      border: 1px solid var(--cms-header-action-border);
      border-radius: 999px;
      padding: 6px;
    }

    .logout-btn {
      border-color: var(--cms-header-action-border);
      background: var(--cms-header-action-bg);
      color: var(--cms-header-text);
      font-size: 14px;
      text-transform: capitalize;
      border: 1px solid var(--cms-header-action-border);
      border-radius: 20px;
      padding: 4px 12px;
      min-width: 102px;
    }
  }
`;
const StyledProfileImageWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-block;
  position: relative;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
  }
`;

const Header = () => {
  const { mode, toggleColorMode } = useCmsColorMode();
  const myAuth0 = new auth0.WebAuth({
    domain: process.env.REACT_APP_AUTH0_DOMAIN,
    clientID: process.env.REACT_APP_AUTH0_CLIENT,
  });

  let navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  const loadAdminUserPermissions = async () => {
    try {
      setLoading(true);
      const loadPermissionResponse = await getAdminUserPermissions();
      verifyResponse(loadPermissionResponse);
      
      // Check if response.data exists and is iterable
      if (loadPermissionResponse && loadPermissionResponse.data && Array.isArray(loadPermissionResponse.data)) {
        const permissionList = [];
        for (const permission of loadPermissionResponse.data) {
          if (permission && permission.tag) {
            permissionList.push(permission.tag);
          }
        }
        dispatch(updateUser({ permissions: permissionList }));
      } else {
        console.warn("Permission response data is not in expected format:", loadPermissionResponse);
        dispatch(updateUser({ permissions: [] }));
      }
    } catch (e) {
      console.error("Get Permission Error: ", e);
      dispatch(updateUser({ permissions: [] }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    myAuth0.parseHash(
      { hash: window.location.hash },
      async (err, authResult) => {
        if (err) {
          console.log(err);
          const idToken = localStorage.getItem(SESSION_VARIABLES.ACCESS_TOKEN);
          if (!idToken || idToken === "") {
            navigate("/login");
          }
        }

        if (authResult) {
          //
          localStorage.setItem(
            SESSION_VARIABLES.ACCESS_TOKEN,
            authResult.idToken
          );
          // call login request to backend api
          let loginResponse;
          try {
            loginResponse = await getAdminUserInfo();
            verifyResponse(loginResponse);
            loadAdminUserPermissions();
          } catch (error) {
            console.error("Login Error: ", error);
          }

          // store user info into local
          localStorage.setItem("nickname", authResult.idTokenPayload.nickname);
          localStorage.setItem("name", authResult.idTokenPayload.name);
          localStorage.setItem("email", authResult.idTokenPayload.email);
          localStorage.setItem("picture", authResult.idTokenPayload.picture);
          navigate("/");
        } else {
          const idToken = localStorage.getItem(SESSION_VARIABLES.ACCESS_TOKEN);

          if (!idToken || idToken === "") {
            navigate("/login");
          } else {
            loadAdminUserPermissions();
          }
        }
      }
    );
  }, []);

  return (
    <StyledHeader>
      <div className="logo">
        <Link to={"/"}>
          <img
            src="https://www.myneurogym.com/assets/images/logos/NeuroGym-Horizonal-Logo-white.png"
            alt="NeuroGym"
          />
        </Link>
      </div>
      <div className="header-content">
        <StyledProfileImageWrapper>
          <Link to={"/profile"}>
            <img src={localStorage.getItem("picture")} alt="profile" />
          </Link>
        </StyledProfileImageWrapper>
        <Tooltip title={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
          <IconButton className="theme-toggle-btn" onClick={toggleColorMode} size="small">
            {mode === "dark" ? (
              <LightModeRoundedIcon fontSize="small" />
            ) : (
              <DarkModeRoundedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Link to={"/profile"}>
          <div>Hi {localStorage.getItem("name")}</div>
        </Link>
        <Button
          className="logout-btn"
          variant="text"
          onClick={() => {
            LogoutCallBack();
            setTimeout(() => {
              myAuth0.logout({ returnTo: window.location.origin });
              localStorage.clear();
            });
          }}
        >
          Logout
        </Button>
      </div>
    </StyledHeader>
  );
};

export default Header;
